import { useCallback, useEffect, useState } from "react";
import { clientEnv } from "../env";

/**
 * The Turnstile widget, in one place instead of two.
 *
 * Extracted from `LoginRoute` unchanged, because the password form in account settings needs the
 * same thing for the same reason. Explicit rendering rather than the automatic kind, because the
 * widget has to hand its token to React state and be resettable after a failed attempt. Changing the current password re-verifies it against
 * `/auth/v1/token?grant_type=password`, which is the same endpoint the login form posts to and is
 * therefore behind the same captcha whenever the project has one configured. Two copies of a
 * script loader and a widget lifecycle would drift, and the copy that drifted would be the one
 * nobody was looking at.
 *
 * With no sitekey there is no widget and nothing to wait for. That is the local stack's case, and
 * it is why `pending` is false rather than true when the feature is switched off: a form that waits
 * forever for a captcha that will never render is worse than no captcha.
 */

const TURNSTILE_SCRIPT = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

interface TurnstileApi {
  render: (
    element: HTMLElement,
    options: {
      sitekey: string;
      callback: (token: string) => void;
      "error-callback"?: () => void;
      "expired-callback"?: () => void;
      theme?: "auto" | "light" | "dark";
    }
  ) => string;
  reset: (widgetId: string) => void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

export interface Turnstile {
  /** Rendered only when there is a sitekey. */
  sitekey: string | undefined;
  token: string | null;
  /** True while a configured widget has not answered yet. False when no widget is configured. */
  pending: boolean;
  /** Takes a fresh token. A Turnstile token is single use, so every attempt needs its own. */
  reset: () => void;
}

/**
 * Where the widget goes. A callback ref, and returned beside the record rather than inside it.
 *
 * Beside it because `react-hooks/refs` classifies the whole object as a ref if one of its members
 * reaches a `ref=` attribute, and then every read of `sitekey` or `pending` during render is an
 * error at the call site. Splitting them leaves a plain record that renders and a ref that is only
 * ever handed to React.
 *
 * A callback rather than a ref object because it also removes a timing question: it fires when the
 * element exists, which is exactly the moment the widget can be drawn into it, instead of an effect
 * running and hoping `current` is populated.
 */
export type TurnstileMount = (node: HTMLDivElement | null) => void;

export function useTurnstile(): [Turnstile, TurnstileMount] {
  const sitekey = clientEnv.VITE_TURNSTILE_SITEKEY;
  const [node, setNode] = useState<HTMLDivElement | null>(null);
  const [widgetId, setWidgetId] = useState<string | null>(null);
  const [token, setToken] = useState<string | null>(null);

  /**
   * The widget id is state and not a ref, and that is not a style preference.
   *
   * `react-hooks/refs` refuses a ref read during render, and it treats a value that closes over a
   * ref the same way, so a `reset` holding a ref made every read of this hook's result an error at
   * the call site. Nothing here needs a ref: the id is written once and read by `reset`, which is a
   * re-render nobody notices.
   */
  useEffect(() => {
    if (!sitekey || !node || widgetId !== null) {
      return;
    }

    const render = () => {
      // Two guards, and both are load-bearing. `widgetId` is the ordinary one. `childElementCount`
      // is what survives React's development double-invoke of an effect, where both runs observe the
      // same null state before either has set it and the widget would be drawn into the same element
      // twice.
      if (!node || !window.turnstile || node.childElementCount > 0) {
        return;
      }

      setWidgetId(window.turnstile.render(node, {
        sitekey,
        callback: (value: string) => setToken(value),
        theme: "auto",
        // A token expires after a few minutes. Clearing it is what stops a form from presenting a
        // stale one, which Supabase refuses with the same code as a missing one, so a person would
        // read "wrong password" for a captcha that simply timed out while they typed.
        "expired-callback": () => setToken(null),
        "error-callback": () => setToken(null)
      }));
    };

    if (window.turnstile) {
      render();
      return;
    }

    const existing = document.querySelector<HTMLScriptElement>(`script[src="${TURNSTILE_SCRIPT}"]`);
    if (existing) {
      existing.addEventListener("load", render);
      return () => existing.removeEventListener("load", render);
    }

    const script = document.createElement("script");
    script.src = TURNSTILE_SCRIPT;
    script.async = true;
    script.defer = true;
    script.addEventListener("load", render);
    document.head.appendChild(script);

    return () => script.removeEventListener("load", render);
  }, [sitekey, node, widgetId]);

  const reset = useCallback(() => {
    if (widgetId !== null) {
      window.turnstile?.reset(widgetId);
      setToken(null);
    }
  }, [widgetId]);

  return [{ sitekey, token, pending: Boolean(sitekey) && token === null, reset }, setNode];
}
